import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import AboutBookModal from './AboutBookModal';
import { BACKEND_URL } from '@/config/api';

const CategoryDropdown = ({ 
  title = "Browse by Category",
  filterType = "genre",
  onAddToLibrary,
  onReadNow,
  isExpanded = false,
  onToggleExpand
}) => {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [booksPerRow, setBooksPerRow] = useState(8);

  // Calculate books per row based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setBooksPerRow(4);
      } else if (window.innerWidth < 1024) {
        setBooksPerRow(6);
      } else {
        setBooksPerRow(8);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [filterType]);

  useEffect(() => {
    if (selectedCategory) {
      fetchBooks();
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${BACKEND_URL}/api/reader/categories?filter_type=${filterType}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const cats = data.categories || [];
        setCategories(cats);
        
        if (cats.length > 0) {
          setSelectedCategory(cats[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${BACKEND_URL}/api/reader/books-by-category?category=${encodeURIComponent(selectedCategory.value)}&category_type=${selectedCategory.type}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBooks(data.books || []);
      }
    } catch (error) {
      console.error('Error fetching category books:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const categoryValue = e.target.value;
    const category = categories.find(cat => cat.value === categoryValue);
    if (category) {
      setSelectedCategory(category);
      // Note: We no longer collapse on category change since parent controls expansion
    }
  };

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  if (!selectedCategory) {
    return null;
  }

  // Show one full row when collapsed, all when expanded
  const displayedBooks = isExpanded ? books : books.slice(0, booksPerRow);
  const hasMore = books.length > booksPerRow;
  const remainingCount = books.length - booksPerRow;

  return (
    <>
      <section className="rounded-2xl bg-slate-900/60 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800">
        {/* Header with title, dropdown, and expand toggle */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-base font-semibold sm:text-lg">{title}</h2>
            
            {/* Category Dropdown */}
            <select
              value={selectedCategory.value}
              onChange={handleCategoryChange}
              className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm text-slate-100 border border-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition cursor-pointer"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              {loading ? 'Loading...' : `${books.length} ${books.length === 1 ? 'book' : 'books'}`}
            </span>
            
            {/* Expand/Collapse Toggle */}
            {hasMore && !loading && (
              <button
                onClick={toggleExpand}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium transition"
                data-testid={`${filterType}-expand-btn`}
              >
                {isExpanded ? (
                  <>
                    <span>Show Less</span>
                    <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <span>+{remainingCount} More</span>
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Books Grid */}
        <div className="mt-4">
          {loading ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {[...Array(booksPerRow)].map((_, i) => (
                <div key={i}>
                  <div className="aspect-[2/3] w-full animate-pulse rounded-lg bg-slate-800 mb-2" />
                  <div className="h-3 w-3/4 animate-pulse rounded bg-slate-800 mb-1" />
                  <div className="h-2 w-1/2 animate-pulse rounded bg-slate-800" />
                </div>
              ))}
            </div>
          ) : books.length === 0 ? (
            <div className="rounded-xl bg-slate-950/60 p-6 text-center">
              <BookOpen className="h-10 w-10 mx-auto mb-2 text-slate-600" />
              <p className="text-slate-300 text-sm font-medium mb-1">No books available</p>
              <p className="text-xs text-slate-400">
                {selectedCategory.value === 'all' 
                  ? 'All books are already in your library' 
                  : `No books found in ${selectedCategory.label}`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {displayedBooks.map((book) => (
                <div
                  key={book.id}
                  onClick={() => setSelectedBook(book)}
                  className="group cursor-pointer"
                >
                  <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-slate-800 mb-1.5 ring-1 ring-slate-700 transition group-hover:ring-indigo-500 group-hover:ring-2">
                    {book.cover_url ? (
                      <img
                        src={`${BACKEND_URL}${book.cover_url}?v=${book.cover_updated_at || book.updated_at || Date.now()}`}
                        alt={book.title}
                        className="h-full w-full object-cover transition group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-500">
                        <BookOpen className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-medium text-slate-50 text-[10px] sm:text-xs line-clamp-2 leading-tight mb-0.5">
                    {book.title}
                  </h3>
                  <p className="text-[9px] sm:text-[10px] text-slate-400 line-clamp-1">
                    {book.author}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {selectedBook && (
        <AboutBookModal
          book={selectedBook}
          isOpen={!!selectedBook}
          onClose={() => setSelectedBook(null)}
          isAuthenticated={true}
          onAddToLibrary={onAddToLibrary}
          onReadNow={onReadNow}
        />
      )}
    </>
  );
};

export default CategoryDropdown;
